// A faction's home corner, mirrored from the server (R548).
//
// The server owns the anchors: `world.AnchorOf` places a faction's founding
// water and `world.BearingOf` gives the direction the installations march
// along (R112). The chart's ribbon banners (card 35) name that water, so the
// client must put the ribbon exactly where the server put the faction. This
// leaf is the mirror of `server/internal/world/topology.go` AnchorOf and
// BearingOf, and it is written to be read beside them.
//
// TWO THINGS THE MIRROR MUST COPY AND NOT IMPROVE:
//
//  1. The centre and the radius are both written against the world's WIDTH.
//     The server states why (R395): a faction anchor is a core-world idea,
//     the core world is square to 8.4 px, and moving the ring to the
//     rectangle would shift core generation and break season
//     reproducibility. `anchorOf` therefore takes the height and does not
//     read it. An arcade map places its seats from the map file and never
//     asks this leaf at all.
//  2. The faction ids run from 1. Faction 1 sits at bearing −π/2, which is
//     straight up the screen, and the rest follow clockwise.

/**
 * The direction from the middle of the map to a faction's corner, in
 * radians, for a world of `n` factions.
 *
 * Mirrors `world.BearingOf`. The angle is measured in the map's own frame,
 * where y grows down the screen: −π/2 is up.
 */
export function factionBearing(f: number, n: number): number {
  if (!(n > 0)) return -Math.PI / 2;
  return -Math.PI / 2 + (2 * Math.PI * (f - 1)) / n;
}

/**
 * The world point of a faction's home corner.
 *
 * Mirrors `world.AnchorOf`: the centre is `worldW / 2` on BOTH axes and the
 * radius is `worldW * radiusFrac`, never the height. `worldH` is taken so a
 * caller reads the same argument list it reads everywhere else on the map,
 * and it is deliberately not used — see the note at the head of this file.
 *
 * `radiusFrac` is `data/balance/world.toml` `anchor_radius_frac`, which the
 * client reads as `ANCHOR_RADIUS_FRAC`.
 */
export function anchorOf(
  f: number, n: number, worldW: number, worldH: number, radiusFrac: number,
): { x: number; y: number } {
  const angle = factionBearing(f, n);
  const r = worldW * radiusFrac;
  return {
    x: worldW / 2 + Math.cos(angle) * r,
    y: worldW / 2 + Math.sin(angle) * r,
  };
}
